import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import { useOperationsDashboard, useBdoWorkbench } from "../hooks";
import type {
  OperationsDashboardResponseDto,
  BdoWorkbenchResponseDto,
} from "../operations.types";

const OperationsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isHeadView =
    staffSubRole === "CEO" ||
    staffSubRole === "BUSINESS_HEAD" ||
    staffSubRole === "OPERATIONS";
  const isBdoView = staffSubRole === "BDO";

  const headQuery = useOperationsDashboard({
    enabled: !isBdoView,
  });
  const bdoQuery = useBdoWorkbench({
    enabled: isBdoView,
  });

  if (isBdoView) {
    const { data, isLoading, error } = bdoQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="BDO Workbench"
          message="Loading territory workload and assigned provider actions…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="BDO Workbench"
          message="Unable to load operations dashboard. Please try again."
        />
      );
    }

    const workbench: BdoWorkbenchResponseDto = {
      profile: {
        areaName: data.profile?.areaName ?? "—",
        name: data.profile?.name ?? "—",
        totalProvidersInArea: data.profile?.totalProvidersInArea ?? 0,
        totalCustomersInArea: data.profile?.totalCustomersInArea ?? 0,
      },
      areaSummary: {
        bookingsToday: data.areaSummary?.bookingsToday ?? 0,
        bookingsThisMonth: data.areaSummary?.bookingsThisMonth ?? 0,
        cancellationsThisMonth: data.areaSummary?.cancellationsThisMonth ?? 0,
      },
      providerTasks: {
        providersToOnboard: data.providerTasks?.providersToOnboard ?? 0,
        providersToVisit: data.providerTasks?.providersToVisit ?? 0,
        providersToTrain: data.providerTasks?.providersToTrain ?? 0,
      },
      bookingTasks: {
        bookingsWithIssues: data.bookingTasks?.bookingsWithIssues ?? 0,
        followUpsRequired: data.bookingTasks?.followUpsRequired ?? 0,
      },
      disputeTasks: {
        disputesToReview: data.disputeTasks?.disputesToReview ?? 0,
        highPriorityDisputes: data.disputeTasks?.highPriorityDisputes ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="BDO Workbench"
          message="Assigned provider acquisition, verification follow-up, and area performance actions."
        />

        <SectionCard title="Area profile">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Area"
              value={workbench.profile.areaName}
              helpText={`BDO: ${workbench.profile.name}`}
            />
            <StatCard
              label="Providers in area"
              value={workbench.profile.totalProvidersInArea}
            />
            <StatCard
              label="Customers in area"
              value={workbench.profile.totalCustomersInArea}
            />
          </div>
        </SectionCard>

        <SectionCard title="Territory execution">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Bookings today"
              value={workbench.areaSummary.bookingsToday}
            />
            <StatCard
              label="Bookings this month"
              value={workbench.areaSummary.bookingsThisMonth}
            />
            <StatCard
              label="Cancellations this month"
              value={workbench.areaSummary.cancellationsThisMonth}
            />
          </div>
        </SectionCard>

        <SectionCard title="Provider actions">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Providers to onboard"
              value={workbench.providerTasks.providersToOnboard}
            />
            <StatCard
              label="Providers to visit"
              value={workbench.providerTasks.providersToVisit}
            />
            <StatCard
              label="Providers to train"
              value={workbench.providerTasks.providersToTrain}
            />
          </div>
        </SectionCard>

        <SectionCard title="Operational queue">
          <InfoCard
            title="Bookings and disputes"
            body={`Bookings with issues: ${workbench.bookingTasks.bookingsWithIssues}, Follow-ups required: ${workbench.bookingTasks.followUpsRequired}, Disputes to review: ${workbench.disputeTasks.disputesToReview}, High-priority disputes: ${workbench.disputeTasks.highPriorityDisputes}.`}
          />
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title={isHeadView ? "Operations Head Control Room" : "Operations Dashboard"}
        message="Loading operational overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title={isHeadView ? "Operations Head Control Room" : "Operations Dashboard"}
        message="Unable to load operations dashboard. Please try again."
      />
    );
  }

  const dashboard: OperationsDashboardResponseDto = {
    kpis: {
      activeProviders: data.kpis?.activeProviders ?? 0,
      activeCustomers: data.kpis?.activeCustomers ?? 0,
      bookingsToday: data.kpis?.bookingsToday ?? 0,
      openOperationalIssues: data.kpis?.openOperationalIssues ?? 0,
    },
    supply: {
      providersLive: data.supply?.providersLive ?? 0,
      providersPendingOnboarding: data.supply?.providersPendingOnboarding ?? 0,
      providersSuspended: data.supply?.providersSuspended ?? 0,
    },
    demand: {
      activeCustomers: data.demand?.activeCustomers ?? 0,
      newRegistrationsThisMonth: data.demand?.newRegistrationsThisMonth ?? 0,
      churnedCustomersThisMonth: data.demand?.churnedCustomersThisMonth ?? 0,
    },
    quality: {
      cancelledBookingsThisMonth: data.quality?.cancelledBookingsThisMonth ?? 0,
      rescheduledBookingsThisMonth: data.quality?.rescheduledBookingsThisMonth ?? 0,
      averageProviderRating: data.quality?.averageProviderRating ?? 0,
    },
  };

  const { kpis, supply, demand, quality } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title={isHeadView ? "Operations Head Control Room" : "Operations Dashboard"}
        message="Provider performance, bookings, and market operations overview."
      />

      <SectionCard title="Operations KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard label="Active providers" value={kpis.activeProviders} />
          <StatCard label="Active customers" value={kpis.activeCustomers} />
          <StatCard
            label="Bookings today"
            value={kpis.bookingsToday}
            helpText={`Open operational issues: ${kpis.openOperationalIssues}`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Supply & onboarding">
        <InfoCard
          title="Provider supply"
          body={`Providers live: ${supply.providersLive}, Pending onboarding: ${supply.providersPendingOnboarding}, Suspended providers: ${supply.providersSuspended}.`}
        />
      </SectionCard>

      <SectionCard title="Demand & retention">
        <InfoCard
          title="Customer demand"
          body={`Active customers: ${demand.activeCustomers}, New registrations this month: ${demand.newRegistrationsThisMonth}, Churned customers this month: ${demand.churnedCustomersThisMonth}.`}
        />
      </SectionCard>

      <SectionCard title="Quality & service delivery">
        <InfoCard
          title="Booking quality"
          body={`Cancelled bookings this month: ${quality.cancelledBookingsThisMonth}, Rescheduled bookings this month: ${quality.rescheduledBookingsThisMonth}, Average provider rating: ${quality.averageProviderRating}.`}
        />
      </SectionCard>
    </div>
  );
};

export default OperationsDashboardPage;